import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { instituteService } from '../../../services/instituteService';
import AddBoothModal from './AddBoothModal';
import EditBoothModal from './EditBoothModal';

const BoothManagementPanel = () => {
  const [booths, setBooths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBoothType, setFilterBoothType] = useState('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedBooth, setSelectedBooth] = useState(null);

  useEffect(() => {
    loadBooths();
  }, []);

  const loadBooths = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await instituteService?.getAllInstitutesForAdmin({ approvalStatus: 'approved' });
      setBooths(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load booths');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBooth = async (formData) => {
    try {
      await instituteService?.createInstitute(formData);
      await loadBooths();
      setIsAddModalOpen(false);
    } catch (err) {
      throw err;
    }
  };

  const handleEditBooth = (booth) => {
    setSelectedBooth(booth);
    setIsEditModalOpen(true);
  };

  const handleUpdateBooth = async (boothId, updateData) => {
    try {
      await instituteService?.updateInstituteProfile(boothId, updateData);
      await loadBooths();
      setIsEditModalOpen(false);
      setSelectedBooth(null);
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteBooth = async (boothId, instituteName) => {
    if (!confirm(`Are you sure you want to delete the booth for "${instituteName}"? This action cannot be undone and will remove all associated data.`)) {
      return;
    }

    try {
      await instituteService?.deleteInstitute(boothId);
      await loadBooths();
    } catch (err) {
      alert(err?.message || 'Failed to delete booth');
    }
  };

  const filteredBooths = booths?.filter(booth => {
    const matchesSearch = booth?.instituteName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         booth?.user?.city?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesFilter = filterBoothType === 'all' || booth?.boothType === filterBoothType;
    return matchesSearch && matchesFilter;
  });

  const getBoothTypeBadge = (type) => {
    const badges = {
      free: { color: 'bg-gray-100 text-gray-700', icon: 'Store', label: 'Free' },
      premium: { color: 'bg-yellow-100 text-yellow-700', icon: 'Crown', label: 'Premium' },
      sponsored: { color: 'bg-purple-100 text-purple-700', icon: 'Star', label: 'Sponsored' }
    };
    const badge = badges?.[type] || badges?.free;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${badge?.color}`}>
        <Icon name={badge?.icon} size={12} />
        {badge?.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-3 text-muted-foreground">Loading booths...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground">Booth Management</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage exhibition hall booths - add, edit, or remove institutes</p>
        </div>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          iconName="Plus"
          className="bg-primary text-white hover:bg-primary/90"
        >
          Add New Booth
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Search Booths</label>
            <div className="relative">
              <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                placeholder="Search by institute name or location..."
                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-smooth"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Filter by Booth Type</label>
            <select
              value={filterBoothType}
              onChange={(e) => setFilterBoothType(e?.target?.value)}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-smooth"
            >
              <option value="all">All Booth Types</option>
              <option value="free">Free Booths</option>
              <option value="premium">Premium Booths</option>
              <option value="sponsored">Sponsored Booths</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2">
          <Icon name="AlertCircle" size={20} className="flex-shrink-0 mt-0.5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Booths Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">Institute</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">Booth Type</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">Courses</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">Contact</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-foreground uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredBooths?.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <Icon name="Store" size={48} className="mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No booths found</p>
                    <p className="text-sm text-muted-foreground mt-1">Try adjusting your search or filters</p>
                  </td>
                </tr>
              ) : (
                filteredBooths?.map((booth) => (
                  <tr key={booth?.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                          <img
                            src={booth?.logoUrl || 'https://img.rocket.new/generatedImages/rocket_gen_img_default.png'}
                            alt={`${booth?.instituteName} logo`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{booth?.instituteName}</p>
                          <p className="text-xs text-muted-foreground">{booth?.user?.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Icon name="MapPin" size={14} />
                        <span>{booth?.user?.city || 'N/A'}, {booth?.user?.state || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getBoothTypeBadge(booth?.boothType)}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-muted-foreground">{booth?.courses?.length || 0} courses</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-muted-foreground">
                        {booth?.contactPerson || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditBooth(booth)}
                          iconName="Edit"
                          className="text-primary hover:bg-primary/10"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteBooth(booth?.id, booth?.instituteName)}
                          iconName="Trash2"
                          className="text-red-600 hover:bg-red-50"
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-muted/30 border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-foreground">{booths?.length}</p>
            <p className="text-sm text-muted-foreground">Total Booths</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-700">{booths?.filter(b => b?.boothType === 'free')?.length}</p>
            <p className="text-sm text-muted-foreground">Free Booths</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-yellow-700">{booths?.filter(b => b?.boothType === 'premium')?.length}</p>
            <p className="text-sm text-muted-foreground">Premium Booths</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-purple-700">{booths?.filter(b => b?.boothType === 'sponsored')?.length}</p>
            <p className="text-sm text-muted-foreground">Sponsored Booths</p>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddBoothModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddBooth}
      />

      <EditBoothModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedBooth(null);
        }}
        booth={selectedBooth}
        onSubmit={handleUpdateBooth}
      />
    </div>
  );
};

export default BoothManagementPanel;